(function () {
  'use strict';
  const theme = window.theme || {};
  const STORAGE_KEY = 'ms:box';

  function formatMoney(cents) {
    if (typeof window.MaisonFormatMoney === 'function') return window.MaisonFormatMoney(cents);
    return '€' + ((Number(cents) || 0) / 100).toFixed(2).replace('.', ',');
  }

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function fill(template, values) {
    return String(template).replace(/%(\w+)%/g, (m, k) => (values[k] !== undefined ? values[k] : m));
  }

  class BoxBuilder extends HTMLElement {
    connectedCallback() {
      this.size = Number(this.dataset.size) || 6;
      this.boxStyle = this.dataset.boxStyle === 'tray' ? 'tray' : 'slots';
      this.busy = false;
      this.inCart = new Map();
      this.querySelectorAll('[data-box-product][data-stock]').forEach((card) => {
        this.inCart.set(card.dataset.variantId, Number(card.dataset.inCart) || 0);
      });
      this.selection = this.restore();

      this.t = {
        add: this.dataset.stringAdd || 'Add box to basket',
        more: this.dataset.stringMore || 'Choose %remaining% more',
        progress: this.dataset.stringProgress || '%filled% of %total% chosen',
        empty: this.dataset.stringEmpty || 'Empty space',
        remove: this.dataset.stringRemove || 'Remove %title%',
        added: this.dataset.stringAdded || 'Added to your basket',
        boxLabel: this.dataset.stringBox || 'Build your own box',
        showing: this.dataset.stringShowing || 'Showing %shown% of %total%',
        qvLoading: this.dataset.stringQvLoading || 'Loading',
        qvError: this.dataset.stringQvError || 'That did not load.',
        qvFull: this.dataset.stringQvFull || 'Full details',
        inBox: this.dataset.stringInBox || '%number% in your box',
        short: this.dataset.stringShort || 'One of your cookies sold out just as you added the box, so it was not added. Please choose again.',
        update: this.dataset.stringUpdate || 'Update box in basket',
        editing: this.dataset.stringEditing || 'This box from your basket has %count% of %size% cookies. Add %remaining% more, then press Update box.',
        editingFull: this.dataset.stringEditingFull || 'Your box is complete. Press Update box to put it back in the basket.'
      };
      /* ?box=<id>: an incomplete box in the basket, reopened here so the missing
         cookies can be added to it rather than a fresh box started. */
      this.editing = null;
      this.editingNote = this.querySelector('[data-box-editing]');
      const wanted = new URLSearchParams(location.search).get('box');
      if (wanted) {
        const route = (theme.routes && theme.routes.cart) || '/cart';
        fetch(`${route}.js`).then((res) => res.json()).then((cart) => this.openBox(wanted, cart)).catch(() => {});
      }

      const rawFee = (this.dataset.feeVariant || '').trim();
      const feeMatch = rawFee.match(/(\d{6,})\s*$/);
      this.feeVariant = feeMatch ? feeMatch[1] : null;
      if (rawFee && !this.feeVariant) {
        console.warn('[box-builder] ignoring unusable packaging variant id:', rawFee);
      }

      this.slots = this.querySelector('[data-box-slots]');
      this.progress = this.querySelector('[data-box-progress]');
      this.totalEl = this.querySelector('[data-box-total]');
      this.vatEl = this.querySelector('[data-box-vat]');
      this.submit = this.querySelector('[data-box-submit]');
      this.clear = this.querySelector('[data-box-clear]');
      this.addedNote = this.querySelector('[data-box-added]');
      this.summaryTitle = this.querySelector('[data-box-summary-title]');

      this.addEventListener('click', (e) => {
        const add = e.target.closest('[data-box-add]');
        const remove = e.target.closest('[data-box-remove]');
        const slot = e.target.closest('.box-slot.is-filled, .box-tray__slot.is-filled');

        if (add) this.add(this.readCard(add.closest('[data-box-product]')));
        else if (remove) this.remove(this.readCard(remove.closest('[data-box-product]')).id);
        else if (slot) this.removeAt(Number(slot.dataset.slotIndex));
      });

      if (this.clear) {
        this.clear.addEventListener('click', () => {
          if (this.busy) return;
          this.selection = [];
          this.render();
          this.focusFirstUsable();
          this.announce(fill(this.t.progress, { filled: 0, total: this.size }));
        });
      }

      this.initTabs();
      this.initQuickView();
      this.initDock();
      if (this.submit) this.submit.addEventListener('click', () => this.addToCart());

      this.onCartUpdated = (e) => this.syncCart(e.detail && e.detail.cart);
      this.onPageShow = (e) => {
        if (!e.persisted) return;
        const route = (theme.routes && theme.routes.cart) || '/cart';
        fetch(`${route}.js`).then((res) => res.json()).then((cart) => this.syncCart(cart)).catch(() => {});
      };
      document.addEventListener('cart:updated', this.onCartUpdated);
      window.addEventListener('pageshow', this.onPageShow);

      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdated);
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.onHashChange) window.removeEventListener('hashchange', this.onHashChange);
    }

    readCard(card) {
      return {
        id: card.dataset.variantId,
        title: card.dataset.title,
        price: Number(card.dataset.price),
        image: card.dataset.image,
        url: card.dataset.url
      };
    }

    gridCard(id) {
      return this.querySelector(`.box-builder__grid [data-box-product][data-variant-id="${CSS.escape(String(id))}"]`);
    }

    restore() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(parsed)) return [];
        const kept = [];
        parsed.forEach((item) => {
          if (!item || kept.length >= this.size) return;
          const card = this.gridCard(item.id);
          if (!card || !card.querySelector('[data-box-add]')) return;
          const id = card.dataset.variantId;
          if (kept.filter((i) => i.id === id).length >= this.available(id)) return;
          kept.push(this.readCard(card));
        });
        return kept;
      } catch (err) {
        return [];
      }
    }

    persist() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.selection));
      } catch (err) {
      }
    }

    syncCart(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      const counts = new Map();
      cart.items.forEach((line) => {
        const id = String(line.variant_id);
        counts.set(id, (counts.get(id) || 0) + (Number(line.quantity) || 0));
      });
      /* The box being edited is replaced on submit, so its lines must not
         count against what is available. */
      if (this.editing) {
        cart.items.filter((i) => i.properties && i.properties._box_id === this.editing.id && !i.properties._box_packaging)
          .forEach((line) => { const id = String(line.variant_id); counts.set(id, Math.max(0, (counts.get(id) || 0) - (Number(line.quantity) || 0))); });
      }
      this.inCart = counts;
      if (this.dialog && this.dialog.open) this.dialog.close();
      this.render();
    }

    /* Load an incomplete box from the basket into the builder: its cookies fill
       the slots, its own lines stop counting against stock, and the submit
       swaps the old lines for the finished box. */
    openBox(boxId, cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      const lines = cart.items.filter((i) => i.properties && i.properties._box_id === boxId);
      const cookies = lines.filter((i) => !i.properties._box_packaging);
      if (!cookies.length) return;
      this.editing = { id: boxId, keys: lines.map((i) => i.key), size: Number(cookies[0].properties._box_size) || this.size };
      this.syncCart(cart);
      this.selection = [];
      cookies.forEach((line) => {
        const card = this.gridCard(line.variant_id);
        if (!card) return;
        for (let n = 0; n < line.quantity && this.selection.length < this.size && this.left(card.dataset.variantId) > 0; n++) {
          this.selection.push(this.readCard(card));
        }
      });
      if (this.addedNote) this.addedNote.hidden = true;
      this.render();
      if (this.summaryTitle) this.summaryTitle.scrollIntoView({ block: 'start' });
    }

    available(id) {
      const card = this.gridCard(id);
      if (!card || card.dataset.stock === undefined) return Infinity;
      return Math.max(0, Number(card.dataset.stock) - (this.inCart.get(String(id)) || 0));
    }

    left(id) {
      return this.available(id) - this.selection.filter((i) => i.id === id).length;
    }

    add(item) {
      if (this.busy) return;
      /* Only cookies from the grid go into a box; a finished box opened in the
         quick view is bought on its own. */
      if (!this.gridCard(item.id)) return;
      if (this.selection.length >= this.size) return;
      if (this.left(item.id) <= 0) return;
      if (this.addedNote) this.addedNote.hidden = true;
      this.selection.push(item);
      this.render();
      this.announce(`${item.title} added. ${this.remaining()} to go.`);
    }

    remove(variantId) {
      const index = this.selection.map((i) => i.id).lastIndexOf(variantId);
      if (index === -1) return;
      this.removeAt(index);
    }

    removeAt(index) {
      if (this.busy) return;
      const [removed] = this.selection.splice(index, 1);
      this.render();
      if (removed) this.announce(`${removed.title} removed.`);
    }

    remaining() {
      return this.size - this.selection.length;
    }

    total() {
      return this.selection.reduce((sum, item) => sum + item.price, 0);
    }

    announce(message) {
      const region = this.dialog && this.dialog.open && this.qvStatus
        ? this.qvStatus
        : document.getElementById('A11yStatus');
      if (!region) return;
      region.textContent = '';
      setTimeout(() => { region.textContent = message; }, 60);
    }

    fixFocus(el) {
      if (this.busy || !el || !this.contains(el)) return;
      const now = document.activeElement;
      if (now !== el && now !== document.body) return;
      const isSlot = el.matches('.box-slot, .box-tray__slot');
      const stuck = el.disabled || (isSlot && !el.classList.contains('is-filled'));
      if (!stuck) return;
      const usable = (b) => b && b !== el && !b.disabled && b.getAttribute('aria-disabled') !== 'true' && b.offsetParent !== null;
      const card = el.closest('[data-box-product]');
      const dialog = el.closest('[data-quickview]');
      let target = null;
      if (card) target = [card.querySelector('[data-box-add]'), card.querySelector('[data-box-remove]')].find(usable);
      if (!target && isSlot && this.slots) target = this.slots.querySelector('.box-slot.is-filled, .box-tray__slot.is-filled');
      if (!target && dialog) target = dialog.querySelector('[data-quickview-close]');
      if (!target) target = [this.submit, this.dockSubmit].find(usable) || this.summaryTitle;
      if (target) target.focus();
    }

    render() {
      const active = document.activeElement;
      this.persist();

      if (this.slots) {
        Array.from(this.slots.children).forEach((child, i) => {
          const item = this.selection[i];
          const slot = this.boxStyle === 'tray' ? child.querySelector('.box-tray__slot') : child;
          if (!slot) return;

          if (this.boxStyle === 'tray') {
            const label = item ? fill(this.t.remove, { title: item.title }) : this.t.empty;
            slot.classList.toggle('is-filled', Boolean(item));
            slot.setAttribute('aria-label', label);
            slot.title = item ? label : '';
            slot.tabIndex = item ? 0 : -1;
            if (item) slot.removeAttribute('aria-disabled');
            else slot.setAttribute('aria-disabled', 'true');
            slot.innerHTML = item
              ? `<img src="${esc(item.image)}" alt="" loading="lazy" decoding="async">` +
                `<span class="box-tray__remove" aria-hidden="true">&times;</span>`
              : '<span class="box-tray__hint" aria-hidden="true"></span>';
            return;
          }

          if (item) {
            slot.className = 'box-slot is-filled';
            slot.innerHTML =
              `<img src="${esc(item.image)}" alt="" width="48" height="48" loading="lazy">` +
              `<span class="box-slot__remove" aria-hidden="true">&times;</span>` +
              `<span class="visually-hidden">${esc(fill(this.t.remove, { title: item.title }))}</span>`;
            slot.setAttribute('role', 'button');
            slot.setAttribute('tabindex', '0');
            slot.title = fill(this.t.remove, { title: item.title });
          } else {
            slot.className = 'box-slot';
            slot.innerHTML =
              `<span class="box-slot__empty" aria-hidden="true">+</span>` +
              `<span class="visually-hidden">${esc(this.t.empty)}</span>`;
            slot.removeAttribute('role');
            slot.removeAttribute('tabindex');
            slot.removeAttribute('title');
          }
        });
      }

      const full = this.selection.length >= this.size;
      this.querySelectorAll('[data-box-product]').forEach((card) => {
        const id = card.dataset.variantId;
        const count = this.selection.filter((i) => i.id === id).length;
        const capped = this.left(id) <= 0;
        const countEl = card.querySelector('[data-box-count]');
        const addBtn = card.querySelector('[data-box-add]');
        const removeBtn = card.querySelector('[data-box-remove]');
        const limit = card.querySelector('[data-box-limit]');
        if (countEl) countEl.textContent = count;
        if (addBtn) addBtn.disabled = full || capped;
        if (removeBtn) removeBtn.disabled = count === 0;
        if (limit) {
          limit.hidden = !capped || full;
          if (addBtn && !limit.hidden) addBtn.setAttribute('aria-describedby', limit.id);
          else if (addBtn) addBtn.removeAttribute('aria-describedby');
        }
        card.classList.toggle('is-chosen', count > 0);
      });

      if (this.dialog && this.dialog.open) {
        const qvCard = this.dialog.querySelector('[data-box-product]');
        const line = this.dialog.querySelector('[data-quickview-count]');
        if (qvCard && line) {
          const n = this.selection.filter((i) => i.id === qvCard.dataset.variantId).length;
          line.textContent = fill(this.t.inBox, { number: n });
          line.hidden = n === 0;
        }
      }

      if (this.progress) {
        this.progress.textContent = fill(this.t.progress, { filled: this.selection.length, total: this.size });
      }
      if (this.totalEl) this.totalEl.textContent = formatMoney(this.total());
      if (this.vatEl) {
        const rate = Number(this.vatEl.dataset.rate) || 0;
        const total = this.total();
        this.vatEl.textContent = formatMoney(Math.floor((total * rate + (100 + rate) / 2) / (100 + rate)));
        this.vatEl.closest('[data-box-vat-row]').hidden = total === 0;
      }
      if (this.clear) this.clear.hidden = this.selection.length === 0;

      if (this.dock) {
        const n = this.selection.length;
        this.dockSlots.forEach((dot, i) => dot.classList.toggle('is-filled', i < n));
        if (this.dockProgress) this.dockProgress.textContent = fill(this.t.progress, { filled: n, total: this.size });
        if (this.dockTotal) {
          const money = this.dockTotal.querySelector('[data-box-dock-money]');
          if (money) money.textContent = formatMoney(this.total());
          else this.dockTotal.textContent = formatMoney(this.total()) + (this.dockTotal.dataset.suffix ? ` ${this.dockTotal.dataset.suffix}` : '');
        }
        if (this.dockSubmit) {
          this.dockSubmit.disabled = !full;
          const label = this.dockSubmit.querySelector('.btn__label');
          if (label) label.textContent = full ? (this.editing ? this.t.update : this.t.add) : fill(this.t.more, { remaining: this.remaining() });
        }
      }

      if (this.submit) {
        const label = this.submit.querySelector('.btn__label');
        this.submit.disabled = !full;
        if (label) {
          label.textContent = full ? (this.editing ? this.t.update : this.t.add) : fill(this.t.more, { remaining: this.remaining() });
        }
      }

      if (this.editingNote) {
        this.editingNote.hidden = !this.editing;
        if (this.editing) {
          this.editingNote.textContent = full
            ? this.t.editingFull
            : fill(this.t.editing, { count: this.selection.length, size: this.size, remaining: this.remaining() });
        }
      }

      this.fixFocus(active);
    }

    focusFirstUsable() {
      const candidates = [
        '[data-box-cats]:not([hidden]) [data-box-add]:not([disabled])',
        '[data-box-tabs]:not([hidden]) [data-box-tab]',
        '[data-box-submit]',
      ];
      for (const sel of candidates) {
        const el = this.querySelector(sel);
        if (el && el.offsetParent !== null) { el.focus(); return; }
      }
    }

    initDock() {
      this.dock = this.querySelector('[data-box-dock]');
      if (!this.dock) return;
      this.dockProgress = this.dock.querySelector('[data-box-dock-progress]');
      this.dockTotal = this.dock.querySelector('[data-box-dock-total]');
      this.dockSubmit = this.dock.querySelector('[data-box-dock-submit]');
      this.dockSlots = Array.from(this.dock.querySelectorAll('[data-box-dock-slots] > i'));
      const summary = this.querySelector('.box-builder__summary');
      const jump = this.dock.querySelector('[data-box-dock-jump]');
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (jump && summary) {
        jump.addEventListener('click', () => {
          summary.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'start' });
          if (this.summaryTitle) this.summaryTitle.focus({ preventScroll: true });
        });
      }
      if (this.dockSubmit) this.dockSubmit.addEventListener('click', () => this.addToCart());

      const wide = window.matchMedia('(min-width: 990px)');
      let ctaInView = false;
      let pastEnd = false;
      const update = () => { this.dock.hidden = wide.matches || ctaInView || pastEnd; };
      if ('IntersectionObserver' in window && summary) {
        new IntersectionObserver((entries) => {
          ctaInView = entries[entries.length - 1].isIntersecting;
          update();
        }, { threshold: 0.5 }).observe(this.submit || summary);

        const end = document.createElement('div');
        end.className = 'box-builder__end';
        end.setAttribute('aria-hidden', 'true');
        summary.appendChild(end);
        new IntersectionObserver((entries) => {
          pastEnd = entries[entries.length - 1].isIntersecting;
          update();
        }, { rootMargin: '100000px 0px 0px 0px' }).observe(end);
      }
      wide.addEventListener('change', update);
      update();
    }

    setBusy(on) {
      [this.submit, this.dockSubmit].forEach((btn) => {
        if (!btn) return;
        btn.classList.toggle('is-loading', on);
        if (on) btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
      });
    }

    initQuickView() {
      this.dialog = this.querySelector('[data-quickview]');
      this.qvSlot = this.querySelector('[data-quickview-slot]');
      this.qvStatus = this.querySelector('[data-quickview-status]');
      if (!this.dialog || !this.qvSlot || typeof this.dialog.showModal !== 'function') return;

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-quickview-close]')) { this.dialog.close(); return; }
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        const link = e.target.closest('.box-card a[href*="/products/"]');
        if (!link || link.closest('[data-quickview]') || link.closest('.box-card--direct')) return;
        let href;
        try { href = new URL(link.getAttribute('href'), window.location.href); } catch (err) { return; }
        if (href.origin !== window.location.origin) return;
        e.preventDefault();
        this.openQuickView(href.pathname + href.search);
      });

      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        this.qvToken = (this.qvToken || 0) + 1;
        if (this.qvAbort) this.qvAbort.abort();
        this.qvSlot.innerHTML = '';
        if (this.qvStatus) this.qvStatus.textContent = '';
        this.dialog.removeAttribute('aria-labelledby');
      });
    }

    async openQuickView(url) {
      const token = (this.qvToken = (this.qvToken || 0) + 1);
      if (this.qvAbort) this.qvAbort.abort();
      this.qvAbort = typeof AbortController === 'function' ? new AbortController() : null;
      this.qvSlot.innerHTML = `<p class="quickview__status">${esc(this.t.qvLoading)}</p>`;
      this.dialog.removeAttribute('aria-labelledby');
      if (!this.dialog.open) this.dialog.showModal();
      try {
        const sep = url.includes('?') ? '&' : '?';
        const res = await fetch(`${url}${sep}section_id=quick-view`, { signal: this.qvAbort ? this.qvAbort.signal : undefined });
        if (!res.ok) throw new Error(String(res.status));
        const html = await res.text();
        if (token !== this.qvToken) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const body = doc.querySelector('[data-quickview-body]');
        if (!body) throw new Error('no body');
        const title = body.querySelector('[data-quickview-title]');
        if (title) {
          title.id = `QuickViewTitle-${token}`;
          this.dialog.setAttribute('aria-labelledby', title.id);
        }
        const actions = body.querySelector('.quickview__actions');
        if (actions && actions.querySelector('[data-box-add]')) {
          const line = document.createElement('p');
          line.className = 'quickview__count';
          line.setAttribute('data-quickview-count', '');
          line.hidden = true;
          actions.appendChild(line);
        }
        this.qvSlot.replaceChildren(body);
        const focusable = this.qvSlot.querySelector('[data-box-add], button, a[href]');
        if (focusable) focusable.focus();
        this.render();
      } catch (err) {
        if (token !== this.qvToken || (err && err.name === 'AbortError')) return;
        this.qvSlot.innerHTML =
          `<p class="quickview__status">${esc(this.t.qvError)} ` +
          `<a href="${esc(url)}">${esc(this.t.qvFull)}</a></p>`;
      }
    }

    initTabs() {
      this.tabStrip = this.querySelector('[data-box-tabs]');
      if (!this.tabStrip) return;
      this.tabStrip.hidden = false;
      this.tabStrip.addEventListener('click', (e) => {
        const tab = e.target.closest('[data-box-tab]');
        if (tab) this.filterTo(tab.dataset.boxTab, { push: true });
      });

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      const openFromHash = (e) => {
        let key = '';
        try {
          key = decodeURIComponent((location.hash || '').replace('#', '')).trim();
        } catch (err) {
          return;
        }
        if (!key || !this.querySelector(`[data-box-tab="${CSS.escape(key)}"]`)) return;
        this.filterTo(key);
        if (e) this.tabStrip.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'start' });
      };
      openFromHash();
      this.onHashChange = openFromHash;
      window.addEventListener('hashchange', openFromHash);
    }

    filterTo(key, opts) {
      const rows = this.querySelectorAll('[data-box-cats]');
      let shown = 0;
      rows.forEach((row) => {
        const match = key === 'all' || (row.dataset.boxCats || '').split(' ').includes(key);
        row.hidden = !match;
        if (match) shown += 1;
      });
      this.querySelectorAll('[data-box-tab]').forEach((tab) => {
        tab.setAttribute('aria-pressed', String(tab.dataset.boxTab === key));
      });
      if (opts && opts.push && window.history && history.replaceState) {
        const base = location.pathname + location.search;
        history.replaceState(null, '', key === 'all' ? base : `${base}#${key}`);
      }
      this.announce(fill(this.t.showing, { shown, total: rows.length }));
    }

    showError(message) {
      if (window.MaisonToast) {
        window.MaisonToast.show(message, 'error');
        return;
      }
      const toast = document.getElementById('Toast');
      if (!toast) return;
      toast.querySelector('.toast__message').textContent = message;
      toast.classList.add('toast--error', 'is-visible');
      toast.hidden = false;
      setTimeout(() => {
        toast.classList.remove('is-visible');
        setTimeout(() => { toast.hidden = true; }, 500);
      }, 4000);
    }

    /* Shopify can accept part of a box (a cookie selling out mid-add). A box that
       did not arrive whole is taken back out, so the basket only ever holds full boxes. */
    async verifyBox(boxId) {
      try {
        let cart;
        try { cart = await (await fetch(`${window.theme.routes.cart}.js`)).json(); }
        catch (err) { cart = await (await fetch(`${window.theme.routes.cart}.js`)).json(); }
        const lines = cart.items.filter((i) => i.properties && i.properties._box_id === boxId);
        const count = lines.filter((i) => !i.properties._box_packaging).reduce((n, i) => n + i.quantity, 0);
        if (count === this.size) return true;
        if (lines.length) {
          const updates = {};
          lines.forEach((i) => { updates[i.key] = 0; });
          await fetch(window.theme.routes.cart_update, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates })
          });
          if (window.MaisonCart && window.MaisonCart.refresh) await window.MaisonCart.refresh(false, null, ++window.MaisonCart.seq);
        }
        return false;
      } catch (err) {
        return true;   // cannot check: the basket's own box check still blocks checkout
      }
    }

    async addToCart() {
      if (this.busy) return;
      if (this.selection.length < this.size) return;
      this.busy = true;

      const boxId = `box-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
      const label = `${this.t.boxLabel} · ${this.size}`;

      const merged = new Map();
      this.selection.forEach((item) => {
        const existing = merged.get(item.id);
        if (existing) existing.quantity += 1;
        else merged.set(item.id, { id: Number(item.id), quantity: 1 });
      });

      const items = Array.from(merged.values()).map((line) => ({
        ...line,
        properties: { _box_id: boxId, _box_size: String(this.size), Box: label }
      }));

      if (this.feeVariant) {
        items.push({
          id: Number(this.feeVariant),
          quantity: 1,
          properties: { _box_id: boxId, _box_size: String(this.size), _box_packaging: '1', Box: label }
        });
      }

      this.setBusy(true);
      const editing = this.editing;

      try {
        if (editing && editing.keys.length) {
          const updates = {};
          editing.keys.forEach((key) => { updates[key] = 0; });
          const res = await fetch(window.theme.routes.cart_update, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates })
          });
          if (!res.ok) throw new Error(this.t.short);
          this.editing = null;
        }
        try {
          await window.MaisonCart.add(items);
        } catch (err) {
          if (!this.feeVariant) throw err;
          console.warn('[box-builder] add failed with the packaging line; retrying without it', err);
          await window.MaisonCart.add(items.filter((i) => String(i.id) !== String(this.feeVariant)));
        }
        if (!(await this.verifyBox(boxId))) throw new Error(this.t.short);
        this.busy = false;
        this.setBusy(false);
        this.selection = [];
        if (editing) {
          const url = new URL(location.href); url.searchParams.delete('box');
          history.replaceState(null, '', url.pathname + url.search + url.hash);
        }
        this.render();
        if (this.addedNote) this.addedNote.hidden = false;
        this.announce(this.t.added);
      } catch (err) {
        /* Shopify may have taken part of the box before refusing the rest;
           nothing short of a full box may stay in the basket. */
        await this.verifyBox(boxId).catch(() => {});
        this.busy = false;
        this.setBusy(false);
        this.render();
        const message = window.MaisonUserMessage ? window.MaisonUserMessage(err) : err.message;
        this.showError(message);
        this.announce(message);
      }
    }
  }

  if (!customElements.get('box-builder')) customElements.define('box-builder', BoxBuilder);

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const slot = e.target.closest('.box-slot.is-filled');
    if (!slot || slot.tagName === 'BUTTON') return;
    e.preventDefault();
    slot.click();
  });
})();
